import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@dia/i18n';
import { getDb, newId } from '@dia/db';
import { Button, LangToggle, Screen, StatusPill } from '@dia/ui';
import { useAuth } from '../auth';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '../env';
import { BackLink } from './parts/BackLink';
import { useAccess } from './parts/useAccess';
import { translateText, type TranslateFail } from './logic/translateClient';
import { buildWhatsAppShareLink } from './logic/whatsapp';

type Direction = 'en_es' | 'es_en';
type Entry = { id: string; direction: Direction; source: string; result: string; at: number };

const HISTORY_KEY = 'guest.translator.history';
const HISTORY_MAX = 10;

async function readHistory(): Promise<Entry[]> {
  try {
    const row = await getDb().meta.get(HISTORY_KEY);
    return Array.isArray(row?.value) ? (row.value as Entry[]) : [];
  } catch {
    return [];
  }
}

async function writeHistory(entries: Entry[]): Promise<void> {
  try {
    await getDb().meta.put({ key: HISTORY_KEY, value: entries.slice(0, HISTORY_MAX) });
  } catch {
    // Storage blocked. The result is still on screen.
  }
}

/**
 * Traductor: type, get the other language, copy it, or send it on WhatsApp.
 *
 * It calls the same translate edge function as everything else, so nothing is
 * translated twice and the stay's allowance covers it. Offline it says so
 * plainly and keeps the guest's text where it is. The last ten translations
 * are kept on the phone, so a phrase already translated can be read again on
 * the boat.
 */
export function Traductor() {
  const { t, lang } = useT();
  const { session } = useAuth();
  const access = useAccess();

  const [direction, setDirection] = useState<Direction>(lang === 'es' ? 'es_en' : 'en_es');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [failure, setFailure] = useState<TranslateFail['reason'] | null>(null);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState<Entry[]>([]);

  useEffect(() => {
    void readHistory().then(setHistory);
  }, []);

  const from = direction === 'en_es' ? 'en' : 'es';
  const to = direction === 'en_es' ? 'es' : 'en';

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const body = text.trim();
    if (!body || !session) return;
    setBusy(true);
    setFailure(null);
    setCopied(false);

    const outcome = await translateText({
      url: SUPABASE_URL,
      anonKey: SUPABASE_ANON_KEY,
      token: session.access_token,
      text: body,
      from,
      to,
      // Words for the crew or a boatman are kept plain. Words for the guest are warm.
      audience: to === 'es' ? 'staff' : 'guest',
    });

    if (outcome.ok) {
      setResult(outcome.text);
      const entry: Entry = { id: newId(), direction, source: body, result: outcome.text, at: Date.now() };
      const next = [entry, ...history].slice(0, HISTORY_MAX);
      setHistory(next);
      await writeHistory(next);
    } else {
      setResult('');
      setFailure(outcome.reason);
    }
    setBusy(false);
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const failureText = (reason: TranslateFail['reason']) =>
    reason === 'quota'
      ? t('guest.translator.quota')
      : reason === 'offline'
        ? t('guest.translator.offline')
        : reason === 'session'
          ? t('error.session_expired')
          : t('guest.translator.failed');

  const whatsappLink = result ? buildWhatsAppShareLink(result) : null;
  const canTranslate = Boolean(session && access?.personalOpen);

  return (
    <Screen
      title={t('guest.translator.title')}
      lede={t('guest.translator.lede')}
      actions={<LangToggle />}
      footer={
        canTranslate && (
          <Button
            type="submit"
            form="translator-form"
            block
            size="lg"
            busy={busy}
            busyLabel={t('loading')}
            disabled={!text.trim()}
          >
            {t('guest.translator.go')}
          </Button>
        )
      }
    >
      <BackLink />

      {!session && (
        <p className="mt-2 flex flex-col gap-3 text-inkMuted">
          {t('guest.translator.sign_in_first')}
          <Link to="/entrar" className="text-accent underline underline-offset-4 min-h-tap inline-flex items-center">
            {t('guest.arrive.sign_in')}
          </Link>
        </p>
      )}

      {session && access && !access.personalOpen && (
        <p className="mt-2 text-inkMuted" role="status">
          {t('guest.translator.locked')}
        </p>
      )}

      {session && access?.personalOpen && (
        <div className="flex flex-col gap-8 mt-2">
          <form id="translator-form" onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill tone="info">{t(`guest.translator.direction.${direction}`)}</StatusPill>
              <Button
                variant="quiet"
                onClick={() => setDirection(direction === 'en_es' ? 'es_en' : 'en_es')}
              >
                {t('guest.translator.swap')}
              </Button>
            </div>

            <label className="flex flex-col gap-2">
              <span>{t('guest.translator.input_label')}</span>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                maxLength={800}
                lang={from}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-1p-ignore
                data-lpignore="true"
                className="bg-surface border border-line rounded px-4 py-3 text-ink"
              />
            </label>
          </form>

          {failure && (
            <p role="alert" className="text-warn border-l-2 border-warn pl-4">
              {failureText(failure)}
            </p>
          )}

          {result && (
            <section aria-labelledby="result" className="flex flex-col gap-3 border-t border-line pt-6">
              <h2 id="result" className="font-display text-[1.25em]">
                {t('guest.translator.result')}
              </h2>
              <p className="text-ink text-[1.2em] whitespace-pre-wrap break-words" lang={to}>
                {result}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="secondary" onClick={() => void copy(result)}>
                  {copied ? t('guest.translator.copied') : t('guest.translator.copy')}
                </Button>
                {whatsappLink && (
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center min-h-tap rounded border border-line px-5 text-ink hover:border-accent"
                  >
                    {t('guest.translator.whatsapp')}
                  </a>
                )}
              </div>
            </section>
          )}

          {history.length > 0 && (
            <section aria-labelledby="recent" className="flex flex-col gap-2 border-t border-line pt-6">
              <h2 id="recent" className="font-display text-[1.25em]">
                {t('guest.translator.recent')}
              </h2>
              <p className="text-inkFaint text-caption">{t('guest.translator.recent_note')}</p>
              <ul>
                {history.map((entry) => (
                  <li key={entry.id} className="border-b border-line last:border-b-0 py-3 flex flex-col gap-1">
                    <span className="text-inkFaint text-caption">{entry.source}</span>
                    <span className="text-ink" lang={entry.direction === 'en_es' ? 'es' : 'en'}>
                      {entry.result}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                variant="quiet"
                onClick={() => {
                  setHistory([]);
                  void writeHistory([]);
                }}
              >
                {t('guest.translator.clear')}
              </Button>
            </section>
          )}
        </div>
      )}
    </Screen>
  );
}
